import { useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from "react";
import { weekMinuteAt, weekMoveBlock, weekMoveEdge, type WeekBlock } from "./weekHoursMath";

/** One day column of the hours editor (the discussion polish and hours editor round, 2026-11-02; brief
 *  docs/briefs/discussion-polish-hours-editor-round.md rule 8, the red team's findings 3, 12 and 13). Every block of the day; with `onChange`
 *  each block has a grip at its top and its bottom (real buttons, straddling the edge, `touch-action: none`), dragged with any pointer or moved
 *  with the arrow keys by half an hour; the block's body is dragged with a mouse or pen when it has room (a finger on it still scrolls). The
 *  times follow while dragging and `onChange` runs once, on release. The column is a plain group, never a button (the grips are buttons): a
 *  click on it without a drag picks the day. */
export type EditDayProps = { day: string; dayName: string; blocks: WeekBlock[]; selected: boolean; weekend: boolean; onSelect?: () => void; onChange?: (blocks: WeekBlock[]) => void };
type DragKind = "from" | "to" | "move";
type Drag = { index: number; kind: DragKind; startY: number; top: number; base: WeekBlock[]; moved: boolean };

const STEP = 30;
/** a body shorter than this (an hour) leaves no room between its grips to grab it */
const BODY_ROOM_PX = 20;
const minutesOf = (hhmm: string) => { const [h, m] = hhmm.split(":").map(Number); return (h ?? 0) * 60 + (m ?? 0); };
const px = (minutes: number) => minutes / 3;
const place = (b: WeekBlock): CSSProperties => ({ top: `${px(minutesOf(b.from))}px`, height: `${px(minutesOf(b.to) - minutesOf(b.from))}px` });
const same = (a: WeekBlock[], b: WeekBlock[]) => a.length === b.length && a.every((x, i) => x.from === b[i]!.from && x.to === b[i]!.to);

/** the drag's next list: an edge follows the pointer's height in the column, the body moves by the pointer's travel */
function dragged(d: Drag, clientY: number): WeekBlock[] {
  if (d.kind === "move") return weekMoveBlock(d.base, d.index, (clientY - d.startY) * 3);
  return weekMoveEdge(d.base, d.index, d.kind, weekMinuteAt(clientY - d.top));
}

export function WeekHoursEditDay({ day, dayName, blocks, selected, weekend, onSelect, onChange }: EditDayProps) {
  const column = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  const justDragged = useRef(false);
  const [preview, setPreview] = useState<WeekBlock[] | null>(null);
  const shown = preview ?? blocks;

  const start = (e: PointerEvent<HTMLElement>, index: number, kind: DragKind) => {
    if (!onChange || e.button !== 0 || !column.current) return;
    if (kind === "move" && (e.pointerType === "touch" || e.currentTarget.getBoundingClientRect().height < BODY_ROOM_PX)) return;
    e.preventDefault(); e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const rect = column.current.getBoundingClientRect();
    drag.current = { index, kind, startY: e.clientY, top: rect.top + column.current.clientTop, base: blocks, moved: false };
  };
  const move = (e: PointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d) return;
    const next = dragged(d, e.clientY);
    d.moved = d.moved || Math.abs(e.clientY - d.startY) > 2;
    setPreview(next);
  };
  const end = (e: PointerEvent<HTMLElement>, commit: boolean) => {
    const d = drag.current;
    if (!d) return;
    drag.current = null; setPreview(null);
    if (!d.moved) return;
    justDragged.current = true; window.setTimeout(() => { justDragged.current = false; }, 0);
    const next = dragged(d, e.clientY);
    if (commit && !same(next, d.base)) onChange?.(next);
  };
  const key = (e: KeyboardEvent<HTMLButtonElement>, index: number, edge: "from" | "to") => {
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
    e.preventDefault();
    const b = blocks[index]!;
    const next = weekMoveEdge(blocks, index, edge, minutesOf(b[edge]) + (e.key === "ArrowUp" ? -STEP : STEP));
    if (!same(next, blocks)) onChange?.(next);
  };
  const handlers = { onPointerMove: move, onPointerUp: (e: PointerEvent<HTMLElement>) => end(e, true), onPointerCancel: (e: PointerEvent<HTMLElement>) => end(e, false) };
  const pick = () => { if (!justDragged.current) onSelect?.(); };

  return (
    <div ref={column} className={`ob-weekhours__day${selected ? " ob-weekhours__day--selected" : ""}${weekend ? " ob-weekhours__day--weekend" : ""}${onSelect ? " ob-weekhours__day--pickable" : ""}`}
      role="group" aria-label={dayName} data-day={day} onClick={onSelect ? pick : undefined}>
      {shown.map((b, i) => {
        const words = `${dayName} ${b.from} to ${b.to}`;
        return [
          <span key={`block-${i}`} className={`ob-weekhours__block${onChange ? " ob-weekhours__block--movable" : ""}`} style={place(b)} {...(onChange ? { onPointerDown: (e: PointerEvent<HTMLElement>) => start(e, i, "move"), ...handlers } : {})}>
            <span className="ob-weekhours__time">{b.from} to {b.to}</span>
          </span>,
          ...(onChange ? [
            <button key={`start-${i}`} type="button" className="ob-weekhours__grip ob-weekhours__grip--start" aria-label={`Start of ${words}`} title={`Start of ${words}`} style={{ top: `${px(minutesOf(b.from))}px` }}
              onPointerDown={(e) => start(e, i, "from")} {...handlers} onKeyDown={(e) => key(e, i, "from")} />,
            <button key={`end-${i}`} type="button" className="ob-weekhours__grip ob-weekhours__grip--end" aria-label={`End of ${words}`} title={`End of ${words}`} style={{ top: `${px(minutesOf(b.to))}px` }}
              onPointerDown={(e) => start(e, i, "to")} {...handlers} onKeyDown={(e) => key(e, i, "to")} />,
          ] : []),
        ];
      })}
    </div>
  );
}
